import React, { useState, FormEvent, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import * as RadioGroup from "@radix-ui/react-radio-group";
import Select from "../../../../components/ui/Select";
import Button from "../../../../components/ui/Button";
import Alert from "../../../../components/ui/Alert";
import AppIcon from "../../../../components/AppIcon";
import { getRecognitionFeedbackConfig } from "../../../../api/PreformanceManagement/Feedback";
import { getCompanyRecognitionValues } from "../../../../api/PreformanceManagement/Recognition";
import { searchSkills } from "../../../../api/Employee/skills";
import { FeedbackVisibilityType, FeedbackType, CompanyRecognitionValueView } from "../../../../models/preformanceManagement";

interface Option {
  value: string | number;
  label: string;
  avatar?: string | null;
}

// Present only when the composer is opened pre-filled from a recognition-gap AI draft.
export interface RecognitionComposerAiDraft {
  name: string;
  aiAvailable: boolean;
  noGoals: boolean;
}

interface RecognitionComposerProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSubmit?: (recognition: any) => void;
  isMobile?: boolean;
  employees?: Option[];
  // Q4-f: when set, the composer opens pre-filled and shows the friendly AI-assist banner.
  initialRecipientId?: string | number | null;
  initialMessage?: string;
  initialValues?: string[];
  aiDraft?: RecognitionComposerAiDraft | null;
}

const RecognitionComposer: React.FC<RecognitionComposerProps> = ({
  isOpen = false,
  onClose = () => {},
  onSubmit = () => {},
  isMobile = false,
  employees = [],
  initialRecipientId = null,
  initialMessage = "",
  initialValues = [],
  aiDraft = null
}) => {
  const { t } = useTranslation();
  const [recognitionType, setRecognitionType] = useState<'recognition' | 'coaching' | 'concern'>('recognition');
  const [visibilityType, setVisibilityType] = useState<FeedbackVisibilityType>(FeedbackVisibilityType.Private);
  const [allowPublicRecognitionFeedback, setAllowPublicRecognitionFeedback] = useState<boolean>(false);
  const [companyValues, setCompanyValues] = useState<Option[]>([]);
  const [skillOptions, setSkillOptions] = useState<Option[]>([]);
  const [skillInputValue, setSkillInputValue] = useState('');
  const [formData, setFormData] = useState({
    recipient: (initialRecipientId != null
      ? employees.find(e => e.value === initialRecipientId) ?? null
      : null) as Option | null,
    message: initialMessage,
    values: [] as Option[],
    skill: null as Option | null,
  });

  const messageRef = useRef<HTMLTextAreaElement | null>(null);

  // An AI draft exists to be edited, so put the caret at the end of it. This is also the
  // non-visual half of "the draft arrived": scrolling tells sighted users, moving focus
  // tells keyboard and screen-reader users. Draft opens only — a blank composer should not
  // steal focus from whatever the person was doing.
  useEffect(() => {
    if (!aiDraft) return;
    const el = messageRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.setSelectionRange(el.value.length, el.value.length);
  }, [aiDraft]);

  // Suggested values only resolve once the company values load; keep only those that exist as company values.
  useEffect(() => {
    if (initialValues.length === 0 || companyValues.length === 0) return;
    setFormData(prev => ({
      ...prev,
      values: companyValues.filter(cv => initialValues.includes(cv.value as string)),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyValues]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({
    recipient: false,
    message: false,
    values: false
  });

  // Load company privacy configuration and recognition values
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const result = await getRecognitionFeedbackConfig();
        if (result.payload) {
          setAllowPublicRecognitionFeedback(result.payload.allowPublicRecognitionFeedback);
          if (!result.payload.allowPublicRecognitionFeedback) {
            setVisibilityType(FeedbackVisibilityType.Private);
          }
        }
      } catch {
        setAllowPublicRecognitionFeedback(false);
        setVisibilityType(FeedbackVisibilityType.Private);
      }
    };

    const loadRecognitionValues = async () => {
      try {
        const result = await getCompanyRecognitionValues();
        if (result.payload) {
          const mappedValues = result.payload.map((v: CompanyRecognitionValueView) => ({
            value: v.name,
            label: v.name
          }));
          setCompanyValues(mappedValues);
        }
      } catch {
        setCompanyValues([]);
      }
    };

    loadConfig();
    loadRecognitionValues();
  }, []);

  // Search skills as user types
  useEffect(() => {
    const searchSkillsAsync = async () => {
      if (skillInputValue.length < 2) {
        setSkillOptions([]);
        return;
      }
      try {
        const result = await searchSkills(skillInputValue);
        if (result.status === 200 && result.payload) {
          const options = result.payload.map(s => ({
            value: s.name,
            label: s.name
          }));
          setSkillOptions(options);
        }
      } catch {
        // Silently fail skill search
      }
    };

    const debounce = setTimeout(searchSkillsAsync, 300);
    return () => clearTimeout(debounce);
  }, [skillInputValue]);

  const handleMessageChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, message: e.target.value }));
  }, []);

  const handleRecipientChange = useCallback((val: string | number | null) => {
    const selected = employees.find(e => e.value === val) || null;
    setFormData(prev => ({ ...prev, recipient: selected }));
  }, [employees]);

  const handleValuesChange = useCallback((val: (string | number)[] | null) => {
    const selected = companyValues.filter(cv => val?.includes(cv.value));
    setFormData(prev => ({ ...prev, values: selected }));
  }, [companyValues]);

  const handleSkillChange = useCallback((val: string | number | null) => {
    if (!val) {
      setFormData(prev => ({ ...prev, skill: null }));
      return;
    }
    const found = skillOptions.find(s => s.value === val);
    setFormData(prev => ({ ...prev, skill: found || { value: val, label: String(val) } }));
  }, [skillOptions]);

  const handleBlur = useCallback((field: keyof typeof touched) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  }, []);

  const getRecipientError = useCallback(() => {
    return touched.recipient && !formData.recipient ? t('performance.recognition.selectRecipient') : "";
  }, [touched.recipient, formData.recipient, t]);

  const getMessageError = useCallback(() => {
    if (touched.message && !formData.message.trim()) {
      return recognitionType === 'recognition'
        ? t('performance.recognition.enterRecognitionMessage')
        : recognitionType === 'coaching'
          ? t('performance.recognition.enterCoachingMessage')
          : t('performance.recognition.describeConcern');
    }
    if (formData.message.length > 500) {
      return t('performance.recognition.messageMaxLength');
    }
    return "";
  }, [touched.message, formData.message, recognitionType, t]);

  const getValuesError = useCallback(() => {
    return touched.values && formData.values.length === 0 ? t('performance.recognition.selectValue') : "";
  }, [touched.values, formData.values.length, t]);

  const isFormValid = useCallback(() => {
    if (recognitionType === 'coaching' || recognitionType === 'concern') {
      return formData.recipient &&
             formData.message.trim() &&
             formData.message.length <= 500;
    }
    return formData.recipient &&
           formData.message.trim() &&
           formData.values.length > 0 &&
           formData.message.length <= 500;
  }, [recognitionType, formData.recipient, formData.message, formData.values.length]);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    setTouched({
      recipient: true,
      message: true,
      values: true
    });

    if (!isFormValid()) return;

    setIsSubmitting(true);

    const payload = (recognitionType === 'coaching' || recognitionType === 'concern')
      ? {
          recipientId: formData.recipient!.value,
          message: formData.message.trim(),
          type: recognitionType,
          feedbackType: recognitionType === 'coaching' ? FeedbackType.Coaching : FeedbackType.Concern,
          visibilityType: FeedbackVisibilityType.Private
        }
      : {
          recipientId: formData.recipient!.value,
          message: formData.message.trim(),
          values: formData.values.map(v => v.value),
          type: 'recognition',
          visibilityType: visibilityType,
          skillName: formData.skill?.value as string || null
        };

    onSubmit(payload);

    setFormData({ recipient: null, message: "", values: [], skill: null });
    setSkillInputValue('');
    setTouched({ recipient: false, message: false, values: false });
    setIsSubmitting(false);
    onClose();
  }, [formData, isFormValid, onClose, onSubmit, recognitionType, visibilityType]);

  const typeLabels = {
    recognition: t('performance.recognition.typeRecognition'),
    coaching: t('performance.recognition.typeCoaching'),
    concern: t('performance.recognition.typeConcern'),
  };

  const formContent = (
    <div className="flex flex-col gap-6">
      {/* Friendly AI-assist banner — shown only when the message was AI-drafted from a gap row. */}
      {aiDraft && (
        <div
          data-testid="recognition-ai-draft-banner"
          role="status"
          className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2.5 text-xs text-amber-800"
        >
          <div className="flex items-start gap-1.5">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {t('performance.recognition.gaps.aiDraftBanner', { name: aiDraft.name })}
              {!aiDraft.aiAvailable && ` ${t('performance.recognition.gaps.aiDraftFallbackNote')}`}
            </span>
          </div>
          {aiDraft.noGoals && (
            <p className="mt-1.5 ps-5">{t('performance.recognition.gaps.aiDraftNoGoals')}</p>
          )}
        </div>
      )}
      <div className="flex justify-between items-start">
        <h2 className="text-lg font-semibold text-foreground">
          {recognitionType === 'recognition' ? t('performance.recognition.giveRecognition') :
           recognitionType === 'coaching' ? t('performance.recognition.giveCoaching') : t('performance.recognition.giveConcern')}
        </h2>
        <button
          onClick={onClose}
          className="p-1 rounded-md hover:bg-muted text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={t('common.close')}
        >
          <AppIcon name="X" size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">

        {/* TYPE SELECTOR */}
        <fieldset>
          <legend className="text-sm text-muted-foreground mb-2">
            {t('performance.recognition.chooseType')}
          </legend>
          <RadioGroup.Root
            value={recognitionType}
            onValueChange={(val) => setRecognitionType(val as 'recognition' | 'coaching' | 'concern')}
            className="flex flex-wrap gap-4"
          >
            {(['recognition', 'coaching', 'concern'] as const).map((type) => (
              <label key={type} className="flex items-center gap-2 cursor-pointer text-sm">
                <RadioGroup.Item
                  value={type}
                  className="w-4 h-4 rounded-full border border-border data-[state=checked]:border-primary data-[state=checked]:border-[5px] focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                >
                  <RadioGroup.Indicator />
                </RadioGroup.Item>
                {typeLabels[type]}
              </label>
            ))}
          </RadioGroup.Root>
        </fieldset>

        {/* Privacy Notice for Coaching and Concern */}
        {(recognitionType === 'coaching' || recognitionType === 'concern') && (
          <Alert variant="info" icon="Lock" className="mb-2">
            {t('performance.recognition.privateNotice')}
          </Alert>
        )}

        {/* VISIBILITY SELECTOR - Only show for Recognition when company allows public */}
        {allowPublicRecognitionFeedback && recognitionType === 'recognition' && (
          <fieldset>
            <legend className="text-sm text-muted-foreground mb-2">
              {t('performance.recognition.visibility')}
            </legend>
            <RadioGroup.Root
              value={String(visibilityType)}
              onValueChange={(val) => setVisibilityType(Number(val) as FeedbackVisibilityType)}
              className="flex flex-wrap gap-4"
            >
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <RadioGroup.Item
                  value={String(FeedbackVisibilityType.Private)}
                  className="w-4 h-4 rounded-full border border-border data-[state=checked]:border-primary data-[state=checked]:border-[5px] focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                >
                  <RadioGroup.Indicator />
                </RadioGroup.Item>
                <AppIcon name="Lock" size={16} />
                {t('performance.recognition.private')}
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <RadioGroup.Item
                  value={String(FeedbackVisibilityType.Public)}
                  className="w-4 h-4 rounded-full border border-border data-[state=checked]:border-primary data-[state=checked]:border-[5px] focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                >
                  <RadioGroup.Indicator />
                </RadioGroup.Item>
                <AppIcon name="Globe" size={16} />
                {t('performance.recognition.public')}
              </label>
            </RadioGroup.Root>
            <p className="text-xs text-muted-foreground mt-1">
              {visibilityType === FeedbackVisibilityType.Private
                ? t('performance.recognition.privateHint')
                : t('performance.recognition.publicHint')}
            </p>
          </fieldset>
        )}

        {/* RECIPIENT DROPDOWN */}
        <div>
          <Select
            searchable
            options={employees.map(e => ({ value: e.value, label: e.label }))}
            value={formData.recipient?.value ?? null}
            onChange={(val) => handleRecipientChange(val as string | number | null)}
            onOpenChange={() => handleBlur('recipient')}
            placeholder={
              recognitionType === 'recognition' ? t('performance.recognition.whoRecognizing') :
              recognitionType === 'coaching' ? t('performance.recognition.whoCoaching') :
              t('performance.recognition.whoConcern')
            }
            label={
              recognitionType === 'recognition' ? t('performance.recognition.whoRecognizing') :
              recognitionType === 'coaching' ? t('performance.recognition.whoCoaching') :
              t('performance.recognition.whoConcern')
            }
            error={getRecipientError() || undefined}
          />
        </div>

        {/* MESSAGE FIELD */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {recognitionType === 'recognition' ? t('performance.recognition.recognitionMessage') :
             recognitionType === 'coaching' ? t('performance.recognition.coachingMessage') : t('performance.recognition.concernDetails')}
          </label>
          <textarea
            ref={messageRef}
            data-testid="recognition-composer-message"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
            rows={4}
            value={formData.message}
            onChange={handleMessageChange}
            onBlur={() => handleBlur('message')}
            placeholder={
              recognitionType === 'recognition' ? t('performance.recognition.shareImpact') :
              recognitionType === 'coaching' ? t('performance.recognition.shareCoaching') :
              t('performance.recognition.describeConcernPlaceholder')
            }
            maxLength={500}
          />
          <div className="flex justify-between mt-1">
            {getMessageError() ? (
              <p className="text-sm text-destructive">{getMessageError()}</p>
            ) : (
              <span />
            )}
            <p className="text-xs text-muted-foreground">{formData.message.length}/500</p>
          </div>
        </div>

        {/* COMPANY VALUES - Only for recognition */}
        {recognitionType === 'recognition' && (
          <div>
            <Select
              searchable
              multiple
              options={companyValues.map(cv => ({ value: cv.value, label: cv.label }))}
              value={formData.values.map(v => v.value)}
              onChange={(val) => handleValuesChange(val as (string | number)[] | null)}
              onOpenChange={() => handleBlur('values')}
              label={t('performance.recognition.companyValues')}
              placeholder={t('performance.recognition.selectValues')}
              error={getValuesError() || undefined}
            />
          </div>
        )}

        {/* SKILL ENDORSEMENT - Optional, only for recognition */}
        {recognitionType === 'recognition' && (
          <div>
            <p className="text-sm text-muted-foreground mb-1">
              {t('performance.recognition.endorseSkill')}
            </p>
            <Select
              searchable
              options={skillOptions.map(s => ({ value: s.value, label: s.label }))}
              value={formData.skill?.value ?? null}
              onChange={(val) => handleSkillChange(val as string | number | null)}
              placeholder={t('performance.recognition.searchSkill')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('performance.recognition.skillHint')}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} type="button">
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={!isFormValid() || isSubmitting}
          >
            <AppIcon
              name={recognitionType === 'recognition' ? 'Heart' : 'MessageCircle'}
              size={16}
              className="me-2"
            />
            {isSubmitting
              ? t('common.sending')
              : recognitionType === 'recognition' ? t('performance.recognition.sendRecognition') :
                recognitionType === 'coaching' ? t('performance.recognition.sendCoaching') : t('performance.recognition.sendConcern')
            }
          </Button>
        </div>
      </form>
    </div>
  );

  if (!isOpen) return null;

  if (isMobile) {
    return (
      <div className="fixed inset-0 bg-black/50 z-[1000] flex items-end">
        <div className="w-full rounded-t-xl bg-card p-4 max-h-[90vh] overflow-auto max-w-[800px] mx-auto">
          {formContent}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-4 max-w-[800px] mx-auto w-full">
      {formContent}
    </div>
  );
};

export default RecognitionComposer;
